// ============================================
// CONSOMMATION DE L'IA — ÉCRAN
// ============================================
// Demande de Nathan du 19/10/2026 : « quantifier le nombre de tokens que nous
// utilisons à travers le SAV ». Section de l'écran Paramètres, réservée aux
// administrateurs et superviseurs (le serveur refuse les autres : 403).
//
// Sélecteur de période, quatre cartes (jetons, appels, coût estimé, brouillon
// moyen), barres par jour (CSS maison, sans bibliothèque), tableaux par
// fonction et par modèle, dossiers les plus consommateurs. Le coût n'est
// affiché que si les tarifs des modèles sont saisis dans « Tarifs des
// modèles » : sinon « tarifs non renseignés », jamais 0.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, BarChart3, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { Button, Card, Input, Select } from '../../shared/ui';
import { decalerJour, jourParis } from '../../shared/dates';
import {
  PERIODES, chargerConsommation, formatCout, formatDollars, formatNombre, formatPart, jourCourt, jourLong,
  libelleFonction, referenceDossier, type CodePeriode, type DemandePeriode, type RapportConsommation,
} from './consommation';

const TACTILE = 'min-h-[40px] lg:min-h-0';

const aujourdhui = (): string => jourParis();
const decaler = decalerJour;

function messageErreur(e: unknown): string {
  const statut = (e as { status?: number })?.status;
  if (statut === 403) return 'Réservé aux administrateurs et aux superviseurs.';
  return e instanceof Error && e.message ? e.message : 'La consommation n’a pas pu être chargée.';
}

/** Une carte chiffrée : titre, valeur, détail. */
const Carte: React.FC<{ titre: string; valeur: string; detail?: React.ReactNode; testId?: string }> = ({ titre, valeur, detail, testId }) => (
  <Card className="p-4 sm:p-5">
    <p className="text-sm font-semibold text-graphite">{titre}</p>
    <p data-testid={testId} className="tnum mt-2 text-2xl font-extrabold leading-tight tracking-tight text-encre sm:text-3xl">{valeur}</p>
    {detail && <div className="mt-1.5 text-xs text-graphite">{detail}</div>}
  </Card>
);

/** Barres verticales par jour : hauteur proportionnelle aux jetons. */
const BarresParJour: React.FC<{ jours: RapportConsommation['parJour'] }> = ({ jours }) => {
  const max = Math.max(1, ...jours.map(j => j.jetonsTotal));
  const total = jours.reduce((s, j) => s + j.jetonsTotal, 0);
  // Étiquettes : toutes jusqu'à 7 jours, sinon une sur n (et la dernière).
  const pas = jours.length <= 7 ? 1 : Math.ceil(jours.length / 6);
  const plusFort = jours.reduce((a, j) => (j.jetonsTotal > a.jetonsTotal ? j : a), jours[0]);
  return (
    <figure>
      <div
        role="img"
        aria-label={`Jetons par jour, du ${jourLong(jours[0].jour)} au ${jourLong(jours[jours.length - 1].jour)} : ${formatNombre(total)} au total${plusFort && plusFort.jetonsTotal > 0 ? `, maximum le ${jourLong(plusFort.jour)} avec ${formatNombre(plusFort.jetonsTotal)}` : ''}.`}
        className="flex h-40 items-end gap-[2px] border-b border-trait sm:gap-1"
      >
        {jours.map(j => (
          <div key={j.jour} className="group relative flex h-full min-w-0 flex-1 items-end" title={`${jourLong(j.jour)} : ${formatNombre(j.jetonsTotal)} jetons, ${j.appels} appel${j.appels > 1 ? 's' : ''}, ${formatCout(j.coutEstime)}`}>
            <div
              data-testid="barre-jour"
              className="w-full rounded-t-sm bg-sapin-600/80 transition-colors group-hover:bg-sapin-700"
              style={{ height: j.jetonsTotal > 0 ? `${Math.max(2, (j.jetonsTotal / max) * 100)}%` : '0%' }}
            />
          </div>
        ))}
      </div>
      <div aria-hidden className="mt-1.5 flex gap-[2px] text-[11px] text-graphite sm:gap-1">
        {jours.map((j, i) => (
          <span key={j.jour} className="tnum min-w-0 flex-1 overflow-visible whitespace-nowrap text-center">
            {i % pas === 0 || i === jours.length - 1 ? jourCourt(j.jour) : ''}
          </span>
        ))}
      </div>
      <figcaption className="mt-2 text-xs text-graphite">Maximum : {formatNombre(max === 1 && total === 0 ? 0 : max)} jetons sur une journée.</figcaption>
    </figure>
  );
};

/** Barre horizontale de part, de 0 à 100 %. */
const BarrePart: React.FC<{ part: number }> = ({ part }) => (
  <span aria-hidden className="block h-1.5 w-full overflow-hidden rounded-full bg-brume">
    <span className="block h-full rounded-full bg-sapin-600" style={{ width: `${Math.min(100, Math.max(0, part * 100))}%` }} />
  </span>
);

export interface ConsommationIAProps {
  /** Ouvre un dossier depuis le classement (absent : lignes non cliquables). */
  onOuvrirDossier?: (ticketId: string) => void;
  /** Mène au réglage « Tarifs des modèles ». */
  onReglerTarifs?: () => void;
  /** Injectable pour les tests ; par défaut, l'appel au serveur. */
  charger?: (d: DemandePeriode) => Promise<RapportConsommation>;
}

const ConsommationIA: React.FC<ConsommationIAProps> = ({ onOuvrirDossier, onReglerTarifs, charger = chargerConsommation }) => {
  const [periode, setPeriode] = useState<CodePeriode>('30j');
  const [du, setDu] = useState(() => decaler(aujourdhui(), -29));
  const [au, setAu] = useState(() => aujourdhui());
  const [demande, setDemande] = useState<DemandePeriode>({ periode: '30j' });
  const [rapport, setRapport] = useState<RapportConsommation | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const lancer = useCallback((d: DemandePeriode) => {
    let vivant = true;
    setChargement(true);
    setErreur(null);
    Promise.resolve()
      .then(() => charger(d))
      .then(r => { if (vivant) setRapport(r); })
      .catch(e => { if (vivant) { setErreur(messageErreur(e)); setRapport(null); } })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [charger]);

  useEffect(() => lancer(demande), [demande, lancer]);

  const datesInvalides = periode === 'personnalise' && (!du || !au || du > au);

  function choisirPeriode(code: CodePeriode) {
    setPeriode(code);
    if (code !== 'personnalise') setDemande({ periode: code });
  }

  const brouillonEtCo = useMemo(() => {
    if (!rapport) return [];
    return rapport.parFonction.filter(f => f.jetonsTotal > 0).slice(0, 3);
  }, [rapport]);

  const lienTarifs = onReglerTarifs ? (
    <button type="button" onClick={onReglerTarifs} className={`font-semibold text-sapin-700 underline-offset-2 hover:underline ${TACTILE}`}>
      Saisir les tarifs
    </button>
  ) : <span>Réglage « Tarifs des modèles », rubrique Intelligence artificielle.</span>;

  const t = rapport?.totaux;

  return (
    <section aria-labelledby="titre-consommation-ia" className="space-y-5">
      <Card className="p-4 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <h2 id="titre-consommation-ia" className="flex items-center gap-2 text-lg font-semibold text-encre">
              <BarChart3 className="h-5 w-5 text-sapin-700" aria-hidden />
              Consommation de l’IA
            </h2>
            <p className="mt-1 text-sm text-graphite">
              Jetons envoyés au fournisseur et reçus de lui, pour chaque fonction du SAV. Un jeton vaut environ trois quarts de mot.
              Jours comptés à l’heure de Paris.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="block w-full sm:w-56">
              <span className="mb-1 block text-xs font-semibold text-graphite">Période</span>
              <Select
                value={periode}
                onChange={e => choisirPeriode(e.target.value as CodePeriode)}
                options={PERIODES.map(p => ({ value: p.code, label: p.libelle }))}
              />
            </label>
            {periode === 'personnalise' && (
              <>
                <label className="block flex-1 min-w-[9rem] sm:w-40 sm:flex-none">
                  <span className="mb-1 block text-xs font-semibold text-graphite">Du</span>
                  <Input type="date" name="consommation-du" value={du} onChange={e => setDu(e.target.value)} />
                </label>
                <label className="block flex-1 min-w-[9rem] sm:w-40 sm:flex-none">
                  <span className="mb-1 block text-xs font-semibold text-graphite">Au</span>
                  <Input type="date" name="consommation-au" value={au} onChange={e => setAu(e.target.value)} />
                </label>
                <Button className={`max-sm:w-full ${TACTILE}`} disabled={datesInvalides} onClick={() => setDemande({ periode: 'personnalise', du, au })}>
                  Afficher
                </Button>
              </>
            )}
            <Button
              variant="secondary"
              className={`px-2.5 ${TACTILE}`}
              aria-label="Actualiser"
              title="Actualiser"
              disabled={chargement}
              onClick={() => setDemande(d => ({ ...d }))}
            >
              <RefreshCw className={`h-4 w-4 ${chargement ? 'animate-spin' : ''}`} aria-hidden />
            </Button>
          </div>
        </div>
        {datesInvalides && <p role="alert" className="mt-2 text-xs font-medium text-statut-urgent">La date de début doit précéder la date de fin.</p>}
      </Card>

      {chargement && !rapport && (
        <div role="status" className="flex items-center justify-center py-12 text-graphite">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden /> Chargement de la consommation…
        </div>
      )}

      {erreur && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{erreur}</span>
        </div>
      )}

      {rapport && t && (
        <div className={`space-y-5 ${chargement ? 'opacity-60' : ''}`} aria-busy={chargement}>
          <p className="text-sm text-graphite">
            Du {jourLong(rapport.periode.du)} au {jourLong(rapport.periode.au)} ({rapport.periode.jours} jour{rapport.periode.jours > 1 ? 's' : ''}).
          </p>

          {rapport.tarifs.erreur && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>Les tarifs saisis sont illisibles : {rapport.tarifs.erreur} {lienTarifs}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
            <Carte
              titre="Jetons consommés"
              testId="total-jetons"
              valeur={formatNombre(t.jetonsTotal)}
              detail={<>
                {formatNombre(t.jetonsEntree)} en entrée{t.jetonsCache > 0 ? ` (dont ${formatNombre(t.jetonsCache)} en cache)` : ''},{' '}
                {formatNombre(t.jetonsSortie)} en sortie, {formatNombre(t.jetonsRaisonnement)} de raisonnement
              </>}
            />
            <Carte
              titre="Appels à l’IA"
              testId="total-appels"
              valeur={formatNombre(t.appels)}
              detail={t.echecs > 0 ? `dont ${formatNombre(t.echecs)} en échec (erreur ou délai)` : 'aucun échec'}
            />
            <Carte
              titre="Coût estimé"
              testId="cout-estime"
              valeur={t.coutEstime === null ? 'Tarifs non renseignés' : formatDollars(t.coutEstime)}
              detail={t.coutEstime === null
                ? <>
                    {rapport.tarifs.renseignes && t.modelesSansTarif.length > 0
                      ? <span className="block">Sans tarif : {t.modelesSansTarif.join(', ')}.</span>
                      : <span className="block">Aucun tarif saisi pour les modèles utilisés.</span>}
                    {lienTarifs}
                  </>
                : 'selon les tarifs saisis, en dollars'}
            />
            <Carte
              titre="Brouillon moyen"
              testId="moyenne-brouillon"
              valeur={rapport.moyenneJetonsBrouillon === null ? '—' : formatNombre(rapport.moyenneJetonsBrouillon)}
              detail={rapport.moyenneJetonsBrouillon === null ? 'aucun brouillon sur la période' : 'jetons par brouillon du conseiller'}
            />
          </div>

          {t.appels === 0 ? (
            <Card className="py-10 text-center">
              <p className="font-medium text-encre">Aucun appel à l’IA sur cette période.</p>
              <p className="mt-1 text-sm text-graphite">Chaque appel est compté dès qu’une fonction IA est utilisée.</p>
            </Card>
          ) : (
            <>
              {brouillonEtCo.length > 0 && (
                <p className="text-sm text-graphite">
                  Principales fonctions :{' '}
                  {brouillonEtCo.map((f, i) => (
                    <span key={f.fonction}>
                      {i > 0 && ' · '}
                      <span className="font-semibold text-encre">{libelleFonction(f.fonction)}</span> {formatPart(f.part)}
                    </span>
                  ))}
                </p>
              )}

              <Card className="p-4 sm:p-6">
                <h3 className="mb-4 text-sm font-semibold text-encre">Jetons par jour</h3>
                <BarresParJour jours={rapport.parJour} />
              </Card>

              <Card className="overflow-hidden">
                <h3 className="border-b border-trait px-4 py-3 text-sm font-semibold text-encre sm:px-6">Par fonction</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs font-semibold text-graphite">
                        <th scope="col" className="px-4 py-2 sm:px-6">Fonction</th>
                        <th scope="col" className="hidden px-3 py-2 text-right sm:table-cell">Appels</th>
                        <th scope="col" className="px-3 py-2 text-right">Jetons</th>
                        <th scope="col" className="hidden w-32 px-3 py-2 md:table-cell">Part</th>
                        <th scope="col" className="px-4 py-2 text-right sm:px-6">Coût</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-trait">
                      {rapport.parFonction.map(f => (
                        <tr key={f.fonction}>
                          <th scope="row" className="px-4 py-2.5 text-left font-medium text-encre sm:px-6">
                            {libelleFonction(f.fonction)}
                            <span className="mt-0.5 block text-xs font-normal text-graphite sm:hidden">
                              {formatNombre(f.appels)} appel{f.appels > 1 ? 's' : ''} · {formatPart(f.part)}
                            </span>
                            {f.echecs > 0 && <span className="mt-0.5 block text-xs font-normal text-statut-attente">{formatNombre(f.echecs)} en échec</span>}
                          </th>
                          <td className="tnum hidden px-3 py-2.5 text-right sm:table-cell">{formatNombre(f.appels)}</td>
                          <td className="tnum px-3 py-2.5 text-right">{formatNombre(f.jetonsTotal)}</td>
                          <td className="hidden px-3 py-2.5 md:table-cell">
                            <span className="flex items-center gap-2"><BarrePart part={f.part} /><span className="tnum w-10 shrink-0 text-right text-xs text-graphite">{formatPart(f.part)}</span></span>
                          </td>
                          <td className={`tnum px-4 py-2.5 text-right sm:px-6 ${f.coutEstime === null ? 'text-xs text-graphite' : ''}`}>{formatCout(f.coutEstime)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              <Card className="overflow-hidden">
                <h3 className="border-b border-trait px-4 py-3 text-sm font-semibold text-encre sm:px-6">Par modèle</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs font-semibold text-graphite">
                        <th scope="col" className="px-4 py-2 sm:px-6">Modèle</th>
                        <th scope="col" className="hidden px-3 py-2 text-right sm:table-cell">Appels</th>
                        <th scope="col" className="hidden px-3 py-2 text-right md:table-cell">Entrée</th>
                        <th scope="col" className="hidden px-3 py-2 text-right md:table-cell">Sortie</th>
                        <th scope="col" className="hidden px-3 py-2 text-right md:table-cell">Raisonnement</th>
                        <th scope="col" className="px-3 py-2 text-right">Total</th>
                        <th scope="col" className="px-4 py-2 text-right sm:px-6">Coût</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-trait">
                      {rapport.parModele.map(m => (
                        <tr key={`${m.fournisseur}|${m.modele}`}>
                          <th scope="row" className="px-4 py-2.5 text-left font-medium text-encre sm:px-6">
                            <span className="break-all">{m.modele}</span>
                            <span className="block text-xs font-normal text-graphite">{m.fournisseur === 'openai' ? 'OpenAI' : m.fournisseur === 'gemini' ? 'Gemini' : m.fournisseur}</span>
                          </th>
                          <td className="tnum hidden px-3 py-2.5 text-right sm:table-cell">{formatNombre(m.appels)}</td>
                          <td className="tnum hidden px-3 py-2.5 text-right md:table-cell">{formatNombre(m.jetonsEntree)}</td>
                          <td className="tnum hidden px-3 py-2.5 text-right md:table-cell">{formatNombre(m.jetonsSortie)}</td>
                          <td className="tnum hidden px-3 py-2.5 text-right md:table-cell">{formatNombre(m.jetonsRaisonnement)}</td>
                          <td className="tnum px-3 py-2.5 text-right">{formatNombre(m.jetonsTotal)}</td>
                          <td className={`tnum px-4 py-2.5 text-right sm:px-6 ${m.coutEstime === null ? 'text-xs text-graphite' : ''}`}>
                            {m.tarifRenseigne ? formatCout(m.coutEstime) : 'tarif non renseigné'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              {rapport.topDossiers.length > 0 && (
                <Card className="overflow-hidden">
                  <h3 className="border-b border-trait px-4 py-3 text-sm font-semibold text-encre sm:px-6">Dossiers les plus consommateurs</h3>
                  <ol className="divide-y divide-trait">
                    {rapport.topDossiers.map((d, i) => {
                      const contenu = (
                        <>
                          <span className="tnum w-5 shrink-0 text-xs font-semibold text-graphite">{i + 1}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold text-encre">{referenceDossier(d)}</span>
                            {d.titre && <span className="block truncate text-xs text-graphite">{d.titre}</span>}
                          </span>
                          <span className="tnum shrink-0 text-right text-sm text-encre">
                            {formatNombre(d.jetonsTotal)}
                            <span className="block text-xs text-graphite">{formatNombre(d.appels)} appel{d.appels > 1 ? 's' : ''}</span>
                          </span>
                        </>
                      );
                      const supprime = d.ticketRef === null && d.ticketNumber === null;
                      return (
                        <li key={d.ticketId}>
                          {onOuvrirDossier && !supprime ? (
                            <button
                              type="button"
                              onClick={() => onOuvrirDossier(d.ticketId)}
                              className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-brume sm:px-6"
                              aria-label={`Ouvrir le dossier ${referenceDossier(d)}`}
                            >
                              {contenu}
                              <ChevronRight className="h-4 w-4 shrink-0 text-graphite" aria-hidden />
                            </button>
                          ) : (
                            <div className="flex items-center gap-3 px-4 py-3 text-sm sm:px-6">{contenu}</div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </Card>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default ConsommationIA;
